import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { runSnapshots } from "@/api/runs";
import { OutcomeBar } from "@/components/outcome-bar";
import { PauseErrorBanner } from "@/components/pause-error-banner";
import { StatusBadge } from "@/components/status-badge";
import { useRuns, useRunSnapshots } from "@/hooks/useEventStream";
import {
  breakdown,
  elapsedSeconds,
  etaSeconds,
  formatDuration,
  formatRate,
  tempo,
} from "@/lib/run-metrics";
import type { RunDetail, RunSnapshot } from "@/api/types";

/**
 * Top-of-page panel that surfaces in-flight runs at a glance with
 * enough visual weight that you can tell at twenty paces "yes,
 * something is working." Hidden entirely when no run is active so the
 * page collapses cleanly.
 *
 * Each active run is one cohesive card: live metrics on top, the full
 * OutcomeBar breakdown embedded below, so the index mirrors the
 * detail-page breakdown a click away.
 */
export function ActiveRunsPanel() {
  const runs = useRuns();
  const active = (runs?.recent_runs ?? []).filter((r) => r.status === "running");
  if (active.length === 0) return null;

  return (
    <section className="space-y-3">
      {active.map((r) => (
        <ActiveRunCard key={r.id} run={r} />
      ))}
    </section>
  );
}

function ActiveRunCard({ run }: { run: RunDetail }) {
  const snaps = useQuery({
    queryKey: ["snapshots", run.id],
    queryFn: () => runSnapshots(run.id),
  });
  const live = useRunSnapshots(run.id);
  const snapshots = mergeSnapshots(snaps.data ?? [], live);

  // Tick once a second so elapsed/ETA stay live without waiting on the
  // next progress event.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const br = breakdown(run);
  const t = tempo(snapshots);
  // A paused run keeps `status: "running"` but the producer isn't
  // feeding and workers are draining what's already queued — suppress
  // the live-pulse heartbeat + ETA so it doesn't look active.
  const isPaused = run.producer_status === "paused";
  const eta = isPaused ? null : etaSeconds(run, snapshots, br);
  const elapsed = elapsedSeconds(run);
  const target = br.effectiveTarget;
  const pct =
    target && target > 0 ? Math.min(100, (br.pushed / target) * 100) : null;

  return (
    <Link
      to="/runs/$runId"
      params={{ runId: run.id }}
      className="ls-active-card relative block rounded-lg border border-border bg-card overflow-hidden hover:border-accent/40 transition-colors"
    >
      {/* Top accent bar — subtle gradient stripe to mark this card as alive */}
      {!isPaused ? (
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-accent/0 via-accent/80 to-accent/0" />
      ) : null}

      <div className="p-5 sm:p-6">
        {/* Header: pulse + status + source + ETA chip + arrow */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-5">
          {!isPaused ? <LivePulse /> : null}
          <StatusBadge status={isPaused ? "paused" : run.status} />
          <span
            className="font-mono text-xs text-muted truncate max-w-[24rem]"
            title={run.source}
          >
            {run.source}
          </span>
          <span className="ml-auto inline-flex items-center gap-2">
            <span
              className="inline-flex items-baseline gap-1.5 rounded-full border border-border bg-subtle/60 px-2.5 py-0.5 text-xs"
              title="Time since this run started."
            >
              <span className="text-[10px] uppercase tracking-wide text-muted">
                Runtime
              </span>
              <span className="tabular-nums font-medium">
                {formatDuration(elapsed)}
              </span>
            </span>
            {eta != null ? (
              <span className="inline-flex items-baseline gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-0.5 text-xs">
                <span className="text-[10px] uppercase tracking-wide text-muted">
                  ETA
                </span>
                <span className="tabular-nums font-medium text-accent">
                  ~{formatDuration(eta)}
                </span>
              </span>
            ) : null}
          </span>
          <ArrowRight className="h-4 w-4 text-muted shrink-0" />
        </div>

        <PauseErrorBanner run={run} className="-mt-2 mb-5" />

        {/* Hero metrics: three big stats — Pushed totals, Pushed rate, and
            Processed rate — co-equal in visual weight. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-5">
          <Metric
            label="Pushed"
            value={br.pushed.toLocaleString()}
            sub={
              target != null
                ? `of ${target.toLocaleString()}${pct != null ? ` · ${pct.toFixed(pct < 1 ? 2 : 1)}%` : ""}`
                : "to Close"
            }
          />
          <Metric
            label="Pushed / hour"
            value={t.pushedPerHour != null ? formatRate(t.pushedPerHour) : "—"}
            sub={
              t.pushedPerMin != null
                ? `${formatRate(t.pushedPerMin)}/min · last 5m`
                : "warming up"
            }
            tone="accent"
          />
          <Metric
            label="Processed / min"
            value={
              t.processedPerMin != null ? formatRate(t.processedPerMin) : "—"
            }
            sub="last 5m"
            tone="accent"
          />
        </div>

        {/* Footer chips: secondary stats */}
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1.5 text-xs">
          <Chip label="Extracted" value={br.extractedTotal.toLocaleString()} />
          <Chip label="Pending" value={br.pending.toLocaleString()} />
          {br.notYetRead > 0 ? (
            <Chip label="Backlog" value={br.notYetRead.toLocaleString()} />
          ) : null}
          {br.duplicate > 0 ? (
            <Chip label="Duplicate" value={br.duplicate.toLocaleString()} />
          ) : null}
        </div>

        <div className="mt-5 pt-5 border-t border-border">
          <OutcomeBar run={run} embedded />
        </div>
      </div>
    </Link>
  );
}

/** Pulsing accent dot — the visual heartbeat. */
function LivePulse() {
  return (
    <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
      <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
    </span>
  );
}

function Metric({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "accent";
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-medium text-muted">
        {label}
      </div>
      <div
        className={`text-3xl sm:text-4xl font-semibold tabular-nums leading-tight ${
          tone === "accent" ? "text-accent" : "text-fg"
        }`}
      >
        {value}
      </div>
      {sub ? (
        <div className="mt-0.5 text-[11px] text-muted tabular-nums">{sub}</div>
      ) : null}
    </div>
  );
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="uppercase tracking-wider text-[10px] text-muted">
        {label}
      </span>
      <span className="tabular-nums font-medium text-fg">{value}</span>
    </span>
  );
}

function mergeSnapshots(hist: RunSnapshot[], live: RunSnapshot[]): RunSnapshot[] {
  const seen = new Set<string>();
  const merged = [...hist, ...live];
  const deduped = merged.filter((s) => {
    if (seen.has(s.at)) return false;
    seen.add(s.at);
    return true;
  });
  deduped.sort((a, b) => a.at.localeCompare(b.at));
  return deduped;
}
